// @flow
import { isEmpty } from 'lodash';
import moment from 'moment';

import { type PriceOfDay, type PricesByDays } from 'Store/calendar/calendar.helper';
import { DEPARTURE_DATE_MAX_DATE } from 'Utils/faceting/constants';

export type PriceResolver = (dayId: number) => PriceOfDay;

export const mixedToMoment = (mixedDate: ?(number | Date | moment$Moment)): moment$Moment => {
  return moment(mixedDate);
};

export const dateToInt = (d: Date, format: string = 'YYYYMMDD') =>
  parseInt(mixedToMoment(d).format(format), 10);

export const dateDiff = (day1: Date, day2: Date) => {
  return Math.abs(moment(day2).diff(moment(day1), 'days'));
};

export const maxDatepickerDate = (): Date =>
  moment()
    .add(DEPARTURE_DATE_MAX_DATE)
    .toDate();

export const minDatepickerDate = (offset: ?number = 1): Date =>
  moment()
    .add({ days: offset })
    .toDate();

function daysWithoutDepartureFactory(resolver: PriceResolver, fromDate: ?Date, toDate: ?Date) {
  return (day: Date) => {
    const { exclusivity = false, noFlight = false, full = false } = resolver(dateToInt(day));

    const available = !exclusivity && !noFlight && !full;

    if (!fromDate && !toDate) {
      return !available;
    }

    if (fromDate && !toDate) {
      return false;
    }

    if (fromDate && toDate) {
      const dayIsSelected =
        fromDate &&
        toDate &&
        dateToInt(day) >= dateToInt(fromDate) &&
        dateToInt(day) <= dateToInt(toDate);

      return !dayIsSelected && !available;
    }
  };
}

const daysWithNoAvailabilityFactory = (resolver: PriceResolver) => {
  return (day: Date) => {
    const data = resolver(dateToInt(day));
    return isEmpty(data) || data.noAvailability;
  };
};

export const closedDaysFactory = (resolver: PriceResolver, fromDate: ?Date, toDate: ?Date) => {
  return (day: Date) => {
    const { closed = false } = resolver(dateToInt(day));

    if (!fromDate && !toDate) {
      return closed;
    }

    if (fromDate && !toDate) {
      return false;
    }

    if (fromDate && toDate) {
      const dayIsSelected =
        fromDate &&
        toDate &&
        dateToInt(day) >= dateToInt(fromDate) &&
        dateToInt(day) <= dateToInt(toDate);

      return !dayIsSelected && closed;
    }
  };
};

const unavailableReturnDaysFactory = (
  fromDate: Date,
  durations: Array<number> = [],
): ((date: Date) => boolean) => {
  return (day: Date) => {
    if (day < fromDate) return false;

    const duration = dateDiff(day, fromDate);

    // rule of max 30 days of reservation
    if (duration > 30) return true;

    return !!duration && !durations.includes(duration);
  };
};

export function getDisabledDays({
  from,
  isSeller,
  optionsDuration,
  prices,
  resolvePrice,
  to,
}: {
  from: ?Date,
  isSeller: boolean,
  optionsDuration: Array<number>,
  prices: ?PricesByDays,
  to: ?Date,
  resolvePrice: PriceResolver,
}) {
  const disabledDays = [];

  const pricesIds = Object.keys(prices || {});
  const hasPrices = pricesIds.length > 0;

  const allClosedDays = pricesIds
    .filter((priceKey: string) => prices && prices[priceKey].closed)
    .map(priceKey => parseInt(priceKey, 10));

  if (hasPrices) {
    const dayIsWithoutDeparture = daysWithoutDepartureFactory(resolvePrice, from, to);
    const dayIsClosed = closedDaysFactory(resolvePrice, from, to);
    const dayIsWithoutData = daysWithNoAvailabilityFactory(resolvePrice);

    disabledDays.push(dayIsWithoutDeparture);
    disabledDays.push(dayIsClosed);
    disabledDays.push(dayIsWithoutData);
  }

  if (from && !to) {
    const fromDay = dateToInt(from);
    const closedDaysAfterFromDay = allClosedDays.filter(closedDay => closedDay > fromDay);
    const firstClosedDay =
      hasPrices &&
      closedDaysAfterFromDay.length > 0 &&
      Math.min.apply(null, closedDaysAfterFromDay);

    disabledDays.push({
      before: moment(from).toDate(),
      after: firstClosedDay
        ? moment(firstClosedDay.toString()).toDate()
        : moment(from)
            .add(30, 'days')
            .toDate(),
    });
  }

  if (from && !to && !isEmpty(optionsDuration) && !isSeller) {
    disabledDays.push(unavailableReturnDaysFactory(from, optionsDuration));
  }

  return disabledDays;
}

export function bestPrice(resolver: PriceResolver) {
  return (day: Date) => {
    const id = dateToInt(day);

    const { isBestPrice } = (resolver && resolver(id)) || { isBestPrice: false };

    return isBestPrice;
  };
}

export const lastRoom = (resolver: PriceResolver) => {
  return (day: Date) => {
    const id = dateToInt(day);

    const { lastAvailability } = (resolver && resolver(id)) || {
      lastAvailability: false,
    };

    return lastAvailability;
  };
};

export function flattenPrices(prices: PricesByDays) {
  const pricesIds = Object.keys(prices);
  return pricesIds.map(priceId => prices[priceId].oldPrice).join();
}
